import { BaseComponent } from '../base-component.js';
import { toast } from '../../client/toast.js';
import { sanitizeUrl } from '../../shared/utils.js';
import { api } from '../../client/api.js';
import { isStorageKey, isDataUrl, isExternalUrl, getS3Key, createStorageKey } from '../../shared/storage-utils.js';
import styles from './ag-link-upload.css?inline';

export class AgLinkUpload extends BaseComponent {
  private currentValue = '';
  private previewUrl = '';
  private uploading = false;
  private imageType: 'profile' | 'background' | 'featured' = 'profile';

  static get observedAttributes() {
    return ['label', 'name', 'value', 'placeholder', 'required', 'image-type'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'image-type' && newValue) {
      const validTypes = ['profile', 'background', 'featured'];
      if (validTypes.includes(newValue)) {
        this.imageType = newValue as 'profile' | 'background' | 'featured';
      }
    }
    if (name === 'value' && newValue !== this.currentValue) {
      this.currentValue = newValue;
      this.previewUrl = this.resolvePreviewUrl(newValue);
      this.render();
    }
    this.render();
  }

  protected styles(): string {
    return styles;
  }

  protected template(): string {
    const uploadingClass = this.uploading ? 'uploading' : '';

    return `
      <div class="link-upload-container ${uploadingClass}">
        <div class="url-input-wrapper">
          <span class="input-label">URL</span>
          <slot name="url-input"></slot>
        </div>
        
        <div class="or-divider">OR</div>
        
        <div class="file-upload-wrapper">
          <span class="input-label">Upload File</span>
          <slot name="file-input"></slot>
          ${this.uploading ? '<span class="upload-status">Uploading...</span>' : ''}
        </div>
      </div>
      
      ${this.previewUrl ? `
        <div class="preview-container">
          <slot name="preview"></slot>
        </div>
      ` : ''}
    `;
  }

  protected afterRender(): void {
    const urlInput = this.querySelector('input[type="url"]') as HTMLInputElement;
    const fileInput = this.querySelector('input[type="file"]') as HTMLInputElement;
    const preview = this.querySelector('img') as HTMLImageElement;

    if (urlInput) {
      urlInput.addEventListener('input', () => {
        this.currentValue = urlInput.value;
        this.previewUrl = this.resolvePreviewUrl(urlInput.value);
        this.updatePreview(preview);
      });
    }

    if (fileInput) {
      fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
          toast.error('Please select a valid image file');
          fileInput.value = '';
          return;
        }

        if (file.size === 0) {
          toast.error('The selected file is empty');
          fileInput.value = '';
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          toast.error('Image must be smaller than 5MB');
          fileInput.value = '';
          return;
        }

        await this.uploadToS3(file, urlInput, preview);
        fileInput.value = '';
      });
    }
  }

  private resolvePreviewUrl(value: string): string {
    if (!value) return '';

    if (isStorageKey(value)) {
      const key = getS3Key(value);
      return api.getStorageUrl(key);
    }

    if (isDataUrl(value) || isExternalUrl(value)) {
      return sanitizeUrl(value);
    }

    return '';
  }

  private async uploadToS3(file: File, urlInput: HTMLInputElement | null, preview: HTMLImageElement | null): Promise<void> {
    this.uploading = true;
    this.render();
    this.afterRender();

    try {
      const result = await api.prepareUpload(this.imageType, file.name, file.type);

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to prepare upload');
      }

      const { url, fields, key } = result.data;

      const formData = new FormData();
      Object.entries(fields).forEach(([fieldKey, value]) => {
        formData.append(fieldKey, value);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(url, {
        method: 'POST',
        body: formData,
        ...((result.data as any).isMock && {
          headers: {
            'Authorization': `Bearer ${api.getToken()}`,
          },
        }),
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const storageKey = createStorageKey(key);
      this.currentValue = storageKey;
      this.previewUrl = this.resolvePreviewUrl(storageKey);

      if (urlInput) {
        urlInput.value = storageKey;
        urlInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      this.updatePreview(preview);
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      this.uploading = false;
      this.render();
      this.afterRender();
    }
  }

  private updatePreview(preview?: HTMLImageElement | null): void {
    if (!preview) {
      preview = this.querySelector('img') as HTMLImageElement;
    }

    if (this.previewUrl && preview) {
      preview.src = this.previewUrl;
      preview.style.display = 'block';
    } else if (preview) {
      preview.style.display = 'none';
    }
  }
}

customElements.define('ag-link-upload', AgLinkUpload);
